import { hasKarteNaviFacts, hasQantNaviFacts, type NaviFacts } from '../contract/facts.js';
import type { MenuGroup, MenuItem, PopoverMenuGroup, ProductMenu } from '../types/menuModel.js';
import { collectConditionPlatforms, evaluateCondition } from './evaluateCondition.js';

export type FilterProductsParams = {
  /**
   * 表示を許可するプロダクト名 (= ProductMenu.name) の集合。
   * ホスト側で `Project.isProductAccessible` 等を使って算出する想定。
   * ここに含まれないプロダクトはメニューから除外される。
   */
  accessibleProductNames: ReadonlySet<string>;
  /**
   * メニュー出し分けの事実 (platform ごとに分離: `{ karte?, qant? }`)。
   * 与えられた platform の述語のみ、各メニュー / プロダクトの `condition` を評価して出し分ける。
   * ある platform の facts が無い場合、その platform の述語を含む condition は評価が
   * スキップされ、accessible のみでフィルタする (= product 単位の表示。後方互換)。
   */
  facts?: NaviFacts;
};

/**
 * condition を満たすか (platform-scoped skip)。
 *
 * condition が参照する全 platform の facts が揃っている場合のみ評価する。
 * 1 つでも欠けていれば condition 全体をスキップ (= 表示) し、旧 backend /
 * bootstrap fallback データとの後方互換を platform 単位で保つ。
 * NOTE: leaf 単位の default 値で評価すると `not(...)` が facts 欠落時に非表示へ倒れうるため、
 * 「ツリー丸ごとスキップ」の all-or-nothing 意味論を platform ごとに維持する。
 * platform 混在 condition (`and(role(...), employee())` 等) も片側欠落で丸ごとスキップされる。
 */
const passesCondition = (condition: MenuItem['condition'], params: FilterProductsParams): boolean => {
  if (!condition) return true;
  const available = {
    karte: hasKarteNaviFacts(params.facts?.karte),
    qant: hasQantNaviFacts(params.facts?.qant),
  };
  for (const platform of collectConditionPlatforms(condition)) {
    if (!available[platform]) return true;
  }
  // params.facts は available 判定を通った platform のみ参照される
  return evaluateCondition(condition, params.facts ?? {});
};

const filterPopoverMenuItem = (item: MenuItem, params: FilterProductsParams): MenuItem | null => {
  if (!passesCondition(item.condition, params)) return null;
  return item;
};

const filterPopoverMenuGroup = (group: PopoverMenuGroup, params: FilterProductsParams): PopoverMenuGroup | null => {
  const items = group.items.map((i) => filterPopoverMenuItem(i, params)).filter((i): i is MenuItem => i !== null);
  if (items.length === 0) return null;
  return { ...group, items };
};

const filterMenuItem = (item: MenuItem, params: FilterProductsParams): MenuItem | null => {
  if (!passesCondition(item.condition, params)) return null;

  // popover trigger の場合は中身も出し分け、空になったら trigger ごと除外する。
  if (item.popoverMenus) {
    const popoverMenus = item.popoverMenus.map((g) => filterPopoverMenuGroup(g, params)).filter((g): g is PopoverMenuGroup => g !== null);
    // 自身に path が無く popover も空なら、リンク先が無いので除外する。
    if (popoverMenus.length === 0 && !item.path) return null;
    return { ...item, popoverMenus };
  }

  // path も popover も無いメニューは href が `#` になるだけなので表示しない。
  if (!item.path) return null;

  return item;
};

const filterMenuGroup = (group: MenuGroup, params: FilterProductsParams): MenuGroup | null => {
  const menus = group.menus.map((m) => filterMenuItem(m, params)).filter((m): m is MenuItem => m !== null);
  if (menus.length === 0) return null;
  return { ...group, menus };
};

const filterProductMenu = (product: ProductMenu, params: FilterProductsParams): ProductMenu | null => {
  if (!params.accessibleProductNames.has(product.name)) return null;
  // L1 (プロダクト自体) の表示条件。
  if (!passesCondition(product.condition, params)) return null;
  const menuGroups = product.menuGroups.map((g) => filterMenuGroup(g, params)).filter((g): g is MenuGroup => g !== null);
  // 外部リンクプロダクト (product.link) はサブメニューを持たず、クリックで直接遷移するため
  // menuGroups が空でも表示する。それ以外はメニューが全滅したら除外する。
  if (menuGroups.length === 0 && !product.link) return null;
  return { ...product, menuGroups };
};

/**
 * ProductMenu の配列を、ホスト側の認可情報に応じてフィルタする。
 * - `accessibleProductNames` に含まれない product を除外
 * - `facts` が与えられた場合、各メニュー / プロダクト / popover item の `condition` を
 *   platform 単位で評価して除外 (Karte 系: v5 parity / QANT 系: v1 ナビ parity)
 * - フィルタの結果メニューが空になった group / product / popover は連鎖的に除外
 */
export const filterProducts = (products: readonly ProductMenu[], params: FilterProductsParams): readonly ProductMenu[] => {
  return products.map((p) => filterProductMenu(p, params)).filter((p): p is ProductMenu => p !== null);
};
